/**
 * 🎯 CareerDestinations — Iter 215 (2026-06-30)
 *
 * 4-column responsive grid showcasing real-world impact across the
 * four verticals the SA confirmed: Hospitality, Logistics, Skilled
 * Trades, Healthcare. Each card carries a candid editorial photo,
 * industry name, one-line placement story, and an average-wage chip.
 *
 * Photos are sourced from the design agent's blueprint (Unsplash +
 * Pexels editorial sets — NOT sterile "smiling-at-laptop" stock).
 */
import { ArrowUpRight } from "lucide-react";

const DESTINATIONS = [
  {
    key:   "hospitality",
    name:  "Hospitality",
    image: "https://images.unsplash.com/photo-1768162125977-9944219971c5?auto=format&fit=crop&w=900&q=80",
    alt:   "Chefs working candidly in a busy professional kitchen, plating service.",
    blurb: "From line cook to sous chef — restaurant, hotel, and resort placements with growth paths.",
    wage:  "$32K–$48K",
  },
  {
    key:   "logistics",
    name:  "Logistics",
    image: "https://images.unsplash.com/photo-1687766011004-8cb676b03bce?auto=format&fit=crop&w=900&q=80",
    alt:   "Warehouse associate scanning inventory in a high-volume distribution center.",
    blurb: "Warehouse, distribution, last-mile delivery — high-throughput partners across the region.",
    wage:  "$38K–$52K",
  },
  {
    key:   "skilled_trades",
    name:  "Skilled Trades",
    image: "https://images.unsplash.com/photo-1622426385889-4fc93a72423a?auto=format&fit=crop&w=900&q=80",
    alt:   "Construction tradesperson in PPE working on a residential build site.",
    blurb: "Apprenticeship-tracked carpentry, HVAC, electrical, and plumbing pathways.",
    wage:  "$45K–$72K",
  },
  {
    key:   "healthcare",
    name:  "Healthcare",
    image: "https://images.pexels.com/photos/8413157/pexels-photo-8413157.jpeg?auto=compress&cs=tinysrgb&w=900",
    alt:   "Nurse in scrubs sharing a quiet moment with a patient in a clinic exam room.",
    blurb: "Certified Nursing Assistants, Medical Office staff, and Behavioral Health roles.",
    wage:  "$40K–$55K",
  },
];

export default function CareerDestinations() {
  return (
    <section
      className="bg-[#FAFAF7] py-24 lg:py-28"
      data-testid="career-destinations"
    >
      <div className="px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto">
        {/* Section header */}
        <div className="mb-14 lg:mb-18 max-w-3xl">
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#1B4332] font-bold mb-4">
            <span className="inline-flex items-center gap-2">
              <span className="h-1.5 w-6 bg-[#1B4332]" />
              Where Our People Land
            </span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0612] leading-tight">
            Career destinations across four industries that{" "}
            <span className="text-[#1B4332]">actually hire transitioning workers.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#0A0612]/70 leading-relaxed">
            A pre-certified, drug-tested, soft-skills-trained workforce ready
            to step onto the floor. Not a referral list — people the world
            counted last, now placed with accountability behind every résumé.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {DESTINATIONS.map((d) => (
            <article
              key={d.key}
              data-testid={`career-card-${d.key}`}
              className="
                group
                bg-white rounded-2xl overflow-hidden
                border border-[#0A0612]/5
                shadow-sm hover:shadow-xl
                hover:-translate-y-1
                transition-all duration-300 ease-out
              "
            >
              <div className="aspect-[4/3] overflow-hidden bg-[#F3F3EE]">
                <img
                  src={d.image}
                  alt={d.alt}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-6 flex flex-col">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display text-2xl font-bold tracking-tight text-[#0A0612]">
                    {d.name}
                  </h3>
                  <ArrowUpRight className="h-5 w-5 text-[#1B4332] mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <p className="mt-3 text-sm text-[#0A0612]/70 leading-relaxed">
                  {d.blurb}
                </p>
                <span
                  className="mt-5 self-start inline-flex items-center gap-1.5 bg-[#1B4332]/8 text-[#1B4332] px-3 py-1.5 rounded-full text-xs font-mono tracking-wider font-semibold"
                  data-testid={`career-wage-chip-${d.key}`}
                >
                  <span className="h-1 w-1 bg-[#1B4332] rounded-full" />
                  {d.wage} avg.
                </span>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
